import moment from "moment";

import {
  get_areas_with_six_hour_forecast,
  getJson,
} from "../api/meteorologicalSea.js";
import { kvToJson } from "@/utils/dafaFormat.js";
import { cloneDeep } from "lodash";
import { feature, featureCollection } from "@turf/turf";
import { parse } from "wellknown";

function isSameDay(date1, date2) {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

// 图标资源在 public 目录下，通过绝对路径加载
function getGaleIconUrl(item) {
  const basePath = process.env.VUE_APP_PUBLIC_BUILD_PATH || "/";
  return `${basePath.replace(/\/?$/, "/")}images/gale/${item}.png`;
}

// 原始图标仅 20x20，直接显示在高分屏上会模糊：
// 放大绘制并收紧 alpha 过渡，让边缘清晰后再交给地图
const GALE_ICON_SCALE = 3;

function sharpenGaleIcon(img, scale) {
  const canvas = document.createElement("canvas");
  canvas.width = img.width * scale;
  canvas.height = img.height * scale;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = imageData.data;
  for (let i = 3; i < d.length; i += 4) {
    d[i] = Math.max(0, Math.min(255, (d[i] - 96) * 3));
  }
  return imageData;
}

export default {
  state: {
    originOptions: {
      zoom: 3.4,
      center: [0, 0],
    },
    gale: {
      marks: {},
      forecastData: {},
    },
    layerList: [
      {
        id: "ehh-wind-fill",
        type: "fill",
        source: "ehh-wind",
        layout: {},
        paint: {
          "fill-opacity": 0.5,
        },
      },
      {
        id: "ehh-wind-storke",
        type: "line",
        source: "ehh-wind",
        layout: {},
        paint: {
          "line-width": 3,
          "line-color": "#ffffff",
        },
      },
      {
        id: "ehh-wind-point",
        type: "symbol",
        source: "ehh-wind",
        layout: {
          "icon-image": ["get", "icon"],
          "icon-size": 1,
        },
        paint: {},
      },
    ],
  },
  actions: {
    galeShow({ state }) {
      if (!window.map) {
        return;
      }
      state.layerList.forEach((layer) => {
        if (window.map.getLayer(layer.id)) {
          window.map.setLayoutProperty(layer.id, "visibility", "visible");
        }
      });
    },
    galeHide({ state }) {
      if (!window.map) {
        return;
      }
      state.layerList.forEach((layer) => {
        if (window.map.getLayer(layer.id)) {
          window.map.setLayoutProperty(layer.id, "visibility", "none");
        }
      });
    },
    galeInit({ state, rootState }, map) {
      return new Promise((resolve) => {
        if (!map || window.map !== map) {
          resolve();
          return;
        }
        const list = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 14, 15, 16];
        const loadingPromises = list.map((item) => {
          if (map.hasImage(item)) {
            return Promise.resolve();
          }
          return new Promise((resolveImg) => {
            map.loadImage(getGaleIconUrl(item), (err, img) => {
              if (!err && map && window.map === map && !map.hasImage(item)) {
                map.addImage(item, sharpenGaleIcon(img, GALE_ICON_SCALE), {
                  pixelRatio: GALE_ICON_SCALE,
                });
              }
              resolveImg();
            });
          });
        });

        Promise.all(loadingPromises).then(() => {
          if (!map.getSource("ehh-wind")) {
            map.addSource("ehh-wind", {
              type: "geojson",
              data: {
                type: "Feature",
                geometry: {
                  type: "Point",
                  coordinates: [],
                },
                properties: {},
              },
            });
          }
          state.layerList.forEach((layer) => {
            if (!map.getLayer(layer.id)) {
              map.addLayer({
                ...layer,
                layout: {
                  ...layer.layout,
                  visibility: "none",
                },
              });
            }
            if (layer.type === "fill" && map.getLayer(layer.id)) {
              map.setPaintProperty(
                layer.id,
                "fill-color",
                rootState.meteorological.fillColorExpressions,
              );
            }
          });
          resolve();
        });
      });
    },
    galeDestroy({ state }, map) {
      if (map && map.getLayer && map.getSource) {
        state.layerList.forEach((layer) => {
          if (map.getLayer(layer.id)) {
            map.removeLayer(layer.id);
          }
        });

        map.getSource("ehh-wind") && map.removeSource("ehh-wind");
      }
      state.gale = {
        marks: {},
        forecastData: {},
      };
    },
    galeGetData({ state }) {
      return new Promise((resolve, reject) => {
        get_areas_with_six_hour_forecast()
          .then((response) => {
            // h5 request 返回响应体本身，大风数据在 result 字段下
            const payload = response?.result || response?.data || {};
            const { k, v } = cloneDeep(payload);
            if (!k || !v) {
              state.gale = {
                marks: {},
                forecastData: [],
              };
              resolve(state.gale);
              return;
            }
            const windData = kvToJson(k, v);

            const times = windData.map(({ forecastTime }) =>
              Number(forecastTime),
            );
            const paths = windData.map((item) => item.path);

            const xAxisData = {};
            let lastDay = new Date("1970-01-01");
            times.map((key, index) => {
              const _key = Number(key);
              if (isSameDay(lastDay, new Date(_key))) {
                xAxisData[_key] = "";
              } else {
                if (paths[index]) {
                  const weekday = [
                    "周日",
                    "周一",
                    "周二",
                    "周三",
                    "周四",
                    "周五",
                    "周六",
                  ];
                  const date = new Date(_key);
                  xAxisData[_key] = `${weekday[date.getDay()]} ${moment(
                    _key,
                  ).format("MM-DD HH:mm")}`;
                  lastDay = date;
                }
              }
            });
            state.gale = {
              min: times.at(0),
              max: times.at(-1),
              marks: xAxisData,
              forecastData: windData,
            };
            resolve(state.gale);
          })
          .catch((e) => reject(e));
      });
    },
    galeOnChange({ state, rootState, dispatch }, val) {
      if (
        !window.map ||
        !window.map.getSource("ehh-wind") ||
        !window.map.getLayer("ehh-wind-fill")
      ) {
        return;
      }
      window.map.setPaintProperty(
        "ehh-wind-fill",
        "fill-color",
        rootState.meteorological.fillColorExpressions,
      );

      const target = Number(val);
      const forecastData = Array.isArray(state.gale.forecastData)
        ? state.gale.forecastData
        : [];
      const wind = forecastData.reduce((selected, item) => {
        const forecastTime = Number(item.forecastTime);
        if (!Number.isFinite(forecastTime) || forecastTime > target) {
          return selected;
        }
        return !selected || forecastTime > Number(selected.forecastTime)
          ? item
          : selected;
      }, null);
      if (wind && wind.path) {
        dispatch("getJson", wind.path);
      }
    },
    getJson({ state, rootState, dispatch }, url) {
      // h5 request 返回响应体本身，无需再解构 data
      getJson(url)
        .then((payload) => {
          if (!window.map || !window.map.getSource("ehh-wind")) {
            return;
          }
          const windArea = kvToJson(payload.windarea.k, payload.windarea.v).map(
            (item) => {
              return feature(parse(item.multipolygon), {
                ...item,
                windZLevel: item.windLevel,
              });
            },
          );
          const windIcon = kvToJson(payload.windicon.k, payload.windicon.v).map(
            (item) => feature(parse(item.point), item),
          );
          const collection = featureCollection([...windArea, ...windIcon]);
          window.map.getSource("ehh-wind").setData(collection);
          dispatch("meteorologyRefreshGalePopup", null, { root: true });
        })
        .catch(() => {});
    },
  },
};
